import React, { useEffect, useRef } from 'react';
import { StaticQuery, graphql } from 'gatsby';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import FontAwesomeIcon from 'react-fontawesome';

const drift = keyframes`
  0% {
    transform: translate(-52%, -48%) scale(1);
    opacity: 0.55;
  }
  100% {
    transform: translate(-48%, -52%) scale(1.25);
    opacity: 0.9;
  }
`;

const breathe = keyframes`
  0%, 100% {
    text-shadow: 0 0 24px rgba(238, 238, 238, 0.08);
  }
  50% {
    text-shadow: 0 0 42px rgba(238, 238, 238, 0.22);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translate3d(0, -0.014em, 0);
  }
  50% {
    transform: translate3d(0, 0.014em, 0);
  }
`;

const flicker = keyframes`
  0%, 82%, 100% {
    opacity: 1;
  }
  83% {
    opacity: 0.42;
  }
  84% {
    opacity: 1;
  }
  86% {
    opacity: 0.72;
  }
  87% {
    opacity: 1;
  }
`;

const emerge = keyframes`
  from {
    opacity: 0;
    filter: blur(14px);
  }
  to {
    opacity: 1;
    filter: blur(0);
  }
`;

const blink = keyframes`
  0%, 45% {
    opacity: 0.7;
  }
  50%, 95% {
    opacity: 0;
  }
  100% {
    opacity: 0.7;
  }
`;

const Void = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
  overflow: hidden;
  background: ${(props) => props.theme.colors.background};
`;

const Aura = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 130vmax;
  height: 130vmax;
  pointer-events: none;
  background: radial-gradient(
    circle at 50% 50%,
    rgba(0, 255, 255, 0.05) 0%,
    rgba(90, 96, 110, 0.12) 22%,
    rgba(0, 0, 0, 0) 58%
  );
  animation: ${drift} 24s ease-in-out infinite alternate;
`;

const Halo = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 52vmax;
  height: 52vmax;
  margin: -26vmax 0 0 -26vmax;
  pointer-events: none;
  opacity: 0;
  transition: opacity 2.5s ease;
  will-change: transform;
  background: radial-gradient(
    circle at 50% 50%,
    rgba(0, 255, 255, 0.04) 0%,
    rgba(150, 160, 180, 0.022) 32%,
    rgba(0, 0, 0, 0) 66%
  );
`;

const Vignette = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
  background: radial-gradient(
    ellipse at center,
    rgba(0, 0, 0, 0) 30%,
    rgba(0, 0, 0, 0.9) 100%
  );
`;

const Grain = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
  opacity: 0.04;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)'/%3E%3C/svg%3E");
`;

const Name = styled.h1`
  position: relative;
  margin: 0;
  font-family: 'JetBrains Mono', 'cascadiacode_light', monospace;
  font-size: clamp(3.25rem, 17vw, 10rem);
  font-weight: 200;
  letter-spacing: 0.14em;
  text-indent: 0.14em;
  color: ${(props) => props.theme.colors.text};
  animation: ${emerge} 3s ease-out both,
    ${float} 14s ease-in-out 3s infinite,
    ${breathe} 9s ease-in-out 3s infinite,
    ${flicker} 17s linear 4s infinite;
`;

const Cursor = styled.span`
  display: inline-block;
  width: 0.07em;
  height: 0.82em;
  margin-left: 0.06em;
  vertical-align: -0.06em;
  background: ${(props) => props.theme.colors.secondary};
  animation: ${blink} 1.8s steps(1, end) 3s infinite both;
`;

const Glyphs = styled.nav`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 4.5rem;
  animation: ${emerge} 3s ease-out 2.4s both;
`;

const Glyph = styled.a`
  margin: 0 1.25rem;
  font-size: 1.5rem;
  line-height: 1;
  color: ${(props) => props.theme.colors.text};
  opacity: 0.3;
  text-decoration: none;
  transition: opacity 0.7s ease, color 0.7s ease, text-shadow 0.7s ease;

  &:hover,
  &:focus {
    opacity: 1;
    color: ${(props) => props.theme.colors.secondary};
    text-shadow: 0 0 18px rgba(0, 255, 255, 0.5);
  }
`;

const Motion = createGlobalStyle`
  @media (prefers-reduced-motion: reduce) {
    * {
      animation: none !important;
    }
  }
`;

const useHalo = () => {
  const halo = useRef(null);

  useEffect(() => {
    const element = halo.current;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const fine = window.matchMedia('(pointer: fine)').matches;

    if (!element || still || !fine) return undefined;

    const target = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const eased = { ...target };
    let frame = null;
    let lit = false;

    const follow = ({ clientX, clientY }) => {
      target.x = clientX;
      target.y = clientY;

      if (!lit) {
        lit = true;
        element.style.opacity = '1';
      }
    };

    const step = () => {
      eased.x += (target.x - eased.x) * 0.04;
      eased.y += (target.y - eased.y) * 0.04;
      element.style.transform = `translate3d(${eased.x}px, ${eased.y}px, 0)`;
      frame = window.requestAnimationFrame(step);
    };

    window.addEventListener('mousemove', follow);
    frame = window.requestAnimationFrame(step);

    return () => {
      window.removeEventListener('mousemove', follow);
      window.cancelAnimationFrame(frame);
    };
  }, []);

  return halo;
};

const LandingPage = () => {
  const halo = useHalo();

  return (
    <StaticQuery
      query={graphql`
        query LandingQuery {
          contentfulAbout {
            socialLinks {
              id
              url
              name
              fontAwesomeIcon
            }
          }
        }
      `}
      render={({ contentfulAbout }) => {
        const { socialLinks } = contentfulAbout;

        return (
          <Void id="home">
            <Motion />
            <Aura />
            <Halo ref={halo} />
            <Grain />
            <Vignette />

            <Name>
              kr-b
              <Cursor />
            </Name>

            <Glyphs>
              {socialLinks.map(({ id, url, name, fontAwesomeIcon }) => (
                <Glyph
                  key={id}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={name}
                >
                  <FontAwesomeIcon name={fontAwesomeIcon} />
                </Glyph>
              ))}
            </Glyphs>
          </Void>
        );
      }}
    />
  );
};

export default LandingPage;
